import React, { memo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import EVCard from '../../../components/EVCard/EVCard';
import LoadingSpinner from '../../../components/LoadingSpinner/LoadingSpinner';
import useVisibleCount from '../../../hooks/useVisibleCount';
import { clampStart, getMaxStart, getVisibleItems } from './carouselPaging';
import greyArrow from '../../assets/images/icons/arrow_carousel_grey.svg';
import blackArrow from '../../assets/images/icons/arrow_carousel_black.svg';
import './VehicleCarousel.scss';

// Defined at module scope so the hook's effect isn't re-run every render.
const BREAKPOINTS = [
  { query: '(max-width: 980px)', count: 1 },
  { query: '(max-width: 1149px)', count: 3 },
];
const DEFAULT_VISIBLE_COUNT = 4;

// The grey asset points left and the black asset points right, so each
// direction flips whichever image doesn't already face the right way.
const CarouselArrow = ({ direction, disabled, onClick, label }) => {
  const flip = direction === 'left' ? !disabled : disabled;

  return (
    <button
      type="button"
      className="btn carousel-arrow"
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
    >
      <img
        src={disabled ? greyArrow : blackArrow}
        className={flip ? 'flip' : undefined}
        alt=""
      />
    </button>
  );
};

CarouselArrow.propTypes = {
  direction: PropTypes.oneOf(['left', 'right']).isRequired,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const VehicleCarousel = ({ electricVehicles }) => {
  const intl = useIntl();
  const visibleCount = useVisibleCount(BREAKPOINTS, DEFAULT_VISIBLE_COUNT);
  const [start, setStart] = useState(0);

  const total = electricVehicles ? electricVehicles.length : 0;

  // Keep the window in range when the list reloads or the viewport shrinks.
  useEffect(() => {
    setStart((current) => clampStart(current, total, visibleCount));
  }, [total, visibleCount]);

  if (!electricVehicles) {
    return (
      <section className="container-fluid grey">
        <LoadingSpinner />
      </section>
    );
  }

  const maxStart = getMaxStart(total, visibleCount);
  const visibleVehicles = getVisibleItems(electricVehicles, start, visibleCount);

  return (
    <section className="discover-section">
      <div className="discover-container">
        <h2 className="title-head">
          <FormattedMessage
            id="homepage.vehicleCarousel.title"
            defaultMessage="There are more electric vehicles than ever. Discover yours."
            description="Vehicle Carousel Title"
          />
        </h2>
        <h3 className="title-sub">
          <FormattedMessage
            id="homepage.vehicleCarousel.subTitle"
            defaultMessage="Compare the cost of your selection to a similar gas vehicle. An EV can save you money because it can be cheaper to own and maintain than a 100% gasoline-powered car."
            description="Vehicle Carousel Subtitle"
          />
        </h3>
        <Link to="/vehicles" className="btn btn-pseg discover-cta" role="button">
          <FormattedMessage
            id="homepage.vehicleCarousel.compareVehiclesButton"
            defaultMessage="Find Your Electric Vehicle"
            description="Find Your Electric Vehicle"
          />
        </Link>
      </div>

      {total > 0 && (
        <div className="car-list-container">
          <div className="car-list row">
            <div className="text-right">
              <CarouselArrow
                direction="left"
                disabled={start === 0}
                onClick={() => setStart((s) => clampStart(s - 1, total, visibleCount))}
                label={intl.formatMessage({
                  id: 'homepage.vehicleCarousel.previous',
                  defaultMessage: 'Previous vehicles',
                })}
              />
            </div>
            <div className="inner-car-slider">
              {visibleVehicles.map((ev) => (
                <EVCard
                  key={ev.handle || ev.electric_vehicle_id}
                  ev={ev}
                  hasEvDetails
                  hasIncentivesAndMatchScore
                />
              ))}
            </div>
            <div>
              <CarouselArrow
                direction="right"
                disabled={start >= maxStart}
                onClick={() => setStart((s) => clampStart(s + 1, total, visibleCount))}
                label={intl.formatMessage({
                  id: 'homepage.vehicleCarousel.next',
                  defaultMessage: 'Next vehicles',
                })}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

VehicleCarousel.propTypes = {
  electricVehicles: PropTypes.array,
};

export default memo(VehicleCarousel);
